/// <amd-module name="Wiki/PageMovePanel/PageMovePanel" />
import * as React from "react";

import { format } from "VSS/Core/Util/String";
import { IVssComponentContext, IVssLayoutManager, VssComponent } from "VSS/Platform/Layout";
import { combineUrlPaths } from "VSS/Platform/Util/Url";

import { ContentSize } from "VSSUI/Callout";
import { FormItem } from "VSSUI/FormItem";
import { Panel } from "VSSUI/Panel";
import { Spinner } from "VSSUI/Spinner";

import * as VCContracts from "Wiki/Clients/VC/Contracts/Git";
import { WikiPage, WikiPageMoveResponse, WikiV2 } from "Wiki/Clients/Wiki/Contracts/Wiki";
import * as Resources from "Wiki/PageMovePanel/Resources";
import { PageRenamePanel } from "Wiki/PageRenamePanel/PageRenamePanel";
import { WikiTree } from "Wiki/PageTree/Components/WikiTree";
import { RootId } from "Wiki/PageTree/Components/WikiTree";
import { FilterTreeState, FilterTreeStore } from "Wiki/PageTree/Stores/FilterTreeStore";
import { WikiPageFilterProvider, WikiPageSearchResults } from "Wiki/PageTree/Utilities/WikiPageFilterProvider";
import { WikiPageTreeItemProvider } from "Wiki/PageTree/WikiPageTreeItemProvider";
import { RepoConstants } from "Wiki/ViewCommon/Constants";
import { getFileName, getParentPath } from "Wiki/ViewCommon/Utils/PathHelper";
import { isIncludeEditingAllowed } from "Wiki/ViewCommon/Utils/WikiHelper";

export interface IPageMovePanelProps {
    wiki: WikiV2;
    version: VCContracts.GitVersionDescriptor;
    title: string;
    onDismiss: () => void;
    sourcePage: WikiPage;
    makePageAsSubPage: (
        wiki: WikiV2,
        version: VCContracts.GitVersionDescriptor,
        sourcePage: WikiPage,
        targetPage: WikiPage | undefined,
        newOrderInParent?: number,
        throwError?: boolean
    ) => Promise<WikiPageMoveResponse>;
    isReorderPagesAllowed: (sourcePage: WikiPage, targetPage: WikiPage) => boolean;
    homePagePath: string;
    selectedPagePath: string | undefined;
    getPagesForFiltering: () => Promise<WikiPage[]>;
    isHandleBrokenLinksEnabled?: boolean;
}

export interface IPageMovePanelState {
    filterState: FilterTreeState;
    isLoadingTree: boolean;
    selectedPagePath?: string;
    isCTAEnabled?: boolean;
    errorMessage?: string;
}

export class PageMovePanel extends VssComponent<IPageMovePanelProps, IPageMovePanelState> {
    private _filterStore: FilterTreeStore;
    private _filterProvider: WikiPageFilterProvider;
    private _treeItemProvider: WikiPageTreeItemProvider;

    constructor(props: IPageMovePanelProps, context: IVssComponentContext) {
        super(props, context);

        this._treeItemProvider = new WikiPageTreeItemProvider([], false);
        this._filterStore = new FilterTreeStore();
        this._filterProvider = new WikiPageFilterProvider(this.context.pageContext, "move-page-tree", this.props.getPagesForFiltering, this._onError);
        this.state = {
            filterState: this._filterStore.getState(),
            isLoadingTree: true
        };
    }

    public componentDidMount(): void {
        super.componentDidMount();
        this.props.getPagesForFiltering().then((allPages: WikiPage[]) => {
            this._treeItemProvider.replaceAllItems(allPages);
            // Creating a dummy root page. We need only gitItemPath for page move hence not populating the other fields.
            const rootPage: WikiPage = {
                gitItemPath: RepoConstants.RootPath,
                id: RootId,
                isNonConformant: false,
                isParentPage: true,
                order: 0,
                subPages: [],
                path: RepoConstants.RootPath,
                remoteUrl: "",
                url: "",
                content: ""
            };
            this._treeItemProvider.addItem(rootPage);
            this.setState({ isLoadingTree: false });
        });

        this._filterStore.addChangedListener(this._updateStateFromStore);
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();
        this._filterStore.removeChangedListener(this._updateStateFromStore);
    }

    public render(): JSX.Element {
        const filterState = this.state.filterState;

        return (
            <Panel
                onDismiss={this.props.onDismiss}
                titleProps={{ text: format(Resources.PageMovePanelHeading, this.props.title), className: "text-ellipsis" }}
                description={!this.props.isHandleBrokenLinksEnabled ? Resources.PageMovePanelDescription : undefined}
                size={ContentSize.Large}
                footerButtonProps={[
                    { text: Resources.Cancel, onClick: this.props.onDismiss },
                    {
                        text: this.props.isHandleBrokenLinksEnabled ? Resources.Next : Resources.Move,
                        primary: true,
                        disabled: !this.state.isCTAEnabled,
                        onClick: this._onMovePage
                    }
                ]}
            >
                <div className={"page-move-panel-content flex-column"}>
                    {this.state.isLoadingTree ? (
                        <Spinner />
                    ) : (
                        <WikiTree
                            homePagePath={this.props.homePagePath}
                            itemProvider={this._treeItemProvider}
                            onPageSelected={this._onPageSelected}
                            onPageToggled={this._onPageToggled}
                            onFilteredPageSelected={this._onPageSelected}
                            onFilterChanged={this._onFilterChanged}
                            onFilteringComplete={this._onFilteringComplete}
                            selectedPagePath={this.state.selectedPagePath}
                            filterTreeItemProvider={filterState.treeItemProvider}
                            isFilterLoading={filterState.isFilterLoading}
                            isFiltering={filterState.isFiltering}
                            getPageIsDisabled={(targetPage: WikiPage) => {
                                return !this.props.isReorderPagesAllowed(this.props.sourcePage, targetPage);
                            }}
                            isIncludeEditingAllowed={isIncludeEditingAllowed(this.context.pageContext)}
                            setInitialFocusToFilter={true}
                            wikiName={this.props.wiki.name}
                            isShowWikiName={true}
                        />
                    )}
                    {this.state.errorMessage && <FormItem message={this.state.errorMessage} error={true} />}
                </div>
            </Panel>
        );
    }

    private _onMovePage = (): void => {
        if (this.state.selectedPagePath) {
            this.setState({
                isCTAEnabled: false
            });

            const targetPage = this._treeItemProvider.getItemFromTree(this.state.selectedPagePath);
            if (targetPage) {
                const { wiki, version, sourcePage } = this.props;

                if (this.props.isHandleBrokenLinksEnabled) {
                    this.props.onDismiss();

                    const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                    layoutManager.renderCallout(onDismiss => {
                        const onSavePromise = () => {
                            return this.props.makePageAsSubPage(
                                wiki,
                                version,
                                sourcePage,
                                targetPage.id !== RootId ? targetPage : undefined,
                                undefined,
                                true
                            );
                        };
                        return (
                            <PageRenamePanel
                                onConfirm={onSavePromise}
                                newPagePath={combineUrlPaths(targetPage.path, getFileName(sourcePage.path))}
                                oldPagePath={sourcePage.path}
                                wiki={wiki}
                                wikiVersion={version}
                                title={format(Resources.MovePagePanel_BrokenLinkFixing_Title, getFileName(sourcePage.path))}
                                description={format(
                                    Resources.MovePagePanel_BrokenLinkFixing_Description,
                                    getParentPath(sourcePage.path),
                                    targetPage.path
                                )}
                                successMessage={Resources.MovePagePanel_BrokenLinkFixing_SuccessMessage}
                                brokenLinksDiscoveryOrRenameFailureMessage={Resources.MovePagePanel_BrokenLinksDiscoveryOrMoveFailureMessage}
                                linksFixFailureMessage={Resources.MovePagePanel_LinksFixFailureMessage}
                                updateButtonText={Resources.Move}
                                onDismiss={onDismiss}
                            />
                        );
                    });
                } else {
                    this.props
                        .makePageAsSubPage(
                            this.props.wiki,
                            this.props.version,
                            this.props.sourcePage,
                            targetPage.id !== RootId ? targetPage : undefined,
                            undefined,
                            true
                        )
                        .then(() => {
                            this.props.onDismiss();
                        }, this._onError);
                }
            }
        }
    };

    private _onPageToggled = (page: WikiPage, isExpanded: boolean) => {
        this._treeItemProvider.toggleItem(page.path, isExpanded);
    };

    private _onPageSelected = (newPage: WikiPage): void => {
        this._onPageToggled(newPage, true);
        this.setState({
            selectedPagePath: newPage.path,
            isCTAEnabled: true
        });
    };

    private _onFilterChanged = (filterText: string): void => {
        this._filterStore.onFilterTextChanged(filterText);
        this._filterProvider.filter(filterText).then((results: WikiPageSearchResults) => {
            this._filterStore.onFilterResultsUpdated({
                filteredPages: results.results,
                exactMatchedPagesCount: results.exactMatchedPagesCount,
                currentSelectedPagePath: this.props.sourcePage.path
            });
        });
    };

    private _onFilteringComplete = (): void => {
        this._treeItemProvider.prepareNextPageToSelect(this.props.sourcePage.path);
        this._filterStore.onFilteringComplete();
    };

    private _updateStateFromStore = (): void => {
        this.setState({
            filterState: this._filterStore.getState()
        });
    };

    private _onError = (e: Error): void => {
        this.setState({ errorMessage: e.message });
    };
}

VssComponent.register("Wiki.PageMovePanel", PageMovePanel);
